/**
 * C5 — fonte ÚNICA de destinos. Alimenta a bottom-nav (mobile), a rail (desktop) e os contadores.
 * Porte literal de `NAV` (Docs/V2/Clinica/clinica/data.js:93): 5 destinos, nesta ordem, com estes
 * rótulos e estes ícones.
 *
 * Duas diferenças deliberadas em relação ao protótipo:
 *
 * 1. `implemented` — só destino com view real entra na navegação renderizada. O protótipo navega
 *    para todas as 5 abas porque todas são mock; em produção, item de menu que leva a tela vazia
 *    é proibido. `router/index.ts` falha no boot se `implemented: true` não tiver view registrada.
 * 2. `badgeKey` aponta para um contador de `GET /portal-summary` (`Badges{Maintenances,Plan,
 *    Clinic}`), NUNCA para uma contagem feita no cliente. Quem conta e quem ordena é o servidor.
 */
export interface Destination {
  /** Rota Vue do destino. */
  readonly path: string;
  readonly name: string;
  /** Rótulo exibido — `label` do NAV do protótipo. */
  readonly title: string;
  /** Glifo do protótipo. Decorativo: sempre acompanhado do rótulo textual e `aria-hidden`. */
  readonly icon: string;
  /** Chave do contador vindo do servidor em `Badges` do portal-summary. */
  readonly badgeKey?: BadgeKey;
  readonly implemented: boolean;
}

/** As três chaves de `PortalSummaryResponse.Badges`. Nenhuma outra existe. */
export type BadgeKey = 'maintenances' | 'plan' | 'clinic';

export const destinations: readonly Destination[] = [
  { path: '/inicio', name: 'inicio', title: 'Início', icon: '🏠', implemented: true },
  { path: '/manutencoes', name: 'manutencoes', title: 'Manutenções', icon: '🔧', badgeKey: 'maintenances', implemented: true },
  { path: '/plano', name: 'plano', title: 'Meu plano', icon: '📄', badgeKey: 'plan', implemented: true },
  { path: '/clinica', name: 'clinica', title: 'Minha clínica', icon: '🏥', badgeKey: 'clinic', implemented: true },
  { path: '/perfil', name: 'perfil', title: 'Perfil', icon: '👤', implemented: true },
];

/** Só o que tem view real — é isto, e não `destinations` inteiro, que vira item clicável. */
export const navigableDestinations: readonly Destination[] = destinations.filter((destination) => destination.implemented);

export function destinationByName(name: string): Destination | undefined {
  return destinations.find((destination) => destination.name === name);
}

/**
 * Abas do "Meu plano", no dialeto capitalizado que o servidor já emite no campo `Route` do
 * portal-summary (`/plano/Faturas`, `/plano/Cota`, …). A forma capitalizada é a CANÔNICA; o
 * router aceita a minúscula e redireciona, para deep link antigo e digitado à mão não quebrar.
 */
export const PLAN_TABS = ['Contrato', 'Faturas', 'Cota', 'Documentos'] as const;
export type PlanTab = typeof PLAN_TABS[number];
export const DEFAULT_PLAN_TAB: PlanTab = 'Contrato';

export function isPlanTab(value: string): value is PlanTab {
  return PLAN_TABS.some((tab) => tab === value);
}

/** Resolve a aba a partir do segmento cru da URL, sem distinguir caixa. `null` = segmento inválido. */
export function resolvePlanTab(segment: string): PlanTab | null {
  return PLAN_TABS.find((tab) => tab.toLowerCase() === segment.toLowerCase()) ?? null;
}
